/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const CompanySettings: React.FC = () => {
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dashboardInputRef = useRef<HTMLInputElement>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [dashboardImagePreviewUrls, setDashboardImagePreviewUrls] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    cin: '',
    pan: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    csr_contact: {
      name: '',
      email: '',
      phone: ''
    },
    logo: null as File | null,
    logo_url: null as string | null,
    dashboard_images: [] as string[],
    new_dashboard_images: [] as File[]
  });

  const uploadDocument = async (file: File, docType: string): Promise<string> => {
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${companyId}/${docType}-${Date.now()}-${safeName}`;
    const { data, error } = await supabase.storage
      .from('company-documents')
      .upload(fileName, file, { upsert: true });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('company-documents')
      .getPublicUrl(fileName);

    return publicUrl;
  };

  useEffect(() => {
    const loadCompanyData = async () => {
      if (!companyId) return;

      try {
        const { data: company, error: companyError } = await supabase
          .from('companies')
          .select('*')
          .eq('id', companyId)
          .single();

        if (companyError) throw companyError;

        if (company) {
          setFormData({
            name: company.name || '',
            cin: company.cin || '',
            pan: company.pan || '',
            address: company.address || {
              street: '',
              city: '',
              state: '',
              pincode: '',
              country: 'India'
            },
            csr_contact: company.csr_contact || {
              name: '',
              email: '',
              phone: ''
            },
            logo: null,
            logo_url: company.logo_url || null,
            dashboard_images: company.dashboard_images || [],
            new_dashboard_images: []
          });

          if (company.logo_url) {
            let path = company.logo_url;
            if (path.includes('/object/public/company-documents/')) {
              path = path.split('/object/public/company-documents/')[1].split('?')[0];
            } else if (path.includes('/company-documents/')) {
              path = path.split('/company-documents/')[1].split('?')[0];
            } else if (path.startsWith('company-documents/')) {
              path = path.replace('company-documents/', '');
            }

            if (path) {
              const decodedPath = decodeURIComponent(path);
              const { data, error } = await supabase.storage.from('company-documents').createSignedUrl(decodedPath, 3600);
              if (error) console.error("Error fetching signed URL on load:", error);
              if (data?.signedUrl) {
                setLogoPreviewUrl(data.signedUrl);
              }
            }
          }

          if (company.dashboard_images && company.dashboard_images.length > 0) {
            const urls = await Promise.all(
              company.dashboard_images.map(async (url: string) => {
                let path = url;
                if (path.includes('/object/public/company-documents/')) path = path.split('/object/public/company-documents/')[1].split('?')[0];
                else if (path.includes('/company-documents/')) path = path.split('/company-documents/')[1].split('?')[0];
                else if (path.startsWith('company-documents/')) path = path.replace('company-documents/', '');
                
                if (path) {
                  const decodedPath = decodeURIComponent(path);
                  const { data } = await supabase.storage.from('company-documents').createSignedUrl(decodedPath, 3600);
                  return data?.signedUrl || null;
                }
                return null;
              })
            );
            setDashboardImagePreviewUrls(urls.filter(Boolean) as string[]);
          }
        }

      } catch (err: any) {
        console.error('Error loading company data:', err);
        setError(err.message || 'Failed to load company data');
      } finally {
        setLoading(false);
      }
    };

    loadCompanyData();
  }, [companyId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [field]: value }
      }));
    } else if (name.startsWith('csr_contact.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        csr_contact: { ...prev.csr_contact, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (!companyId) throw new Error('Company ID not found');

      // Get current company data to check if CIN/PAN is changing
      const { data: currentCompany } = await supabase
        .from('companies')
        .select('cin, pan')
        .eq('id', companyId)
        .single();

      // Check if CIN is being changed and if it conflicts with another company
      if (currentCompany && formData.cin !== currentCompany.cin) {
        const { data: existingCompany } = await supabase
          .from('companies')
          .select('id')
          .eq('cin', formData.cin)
          .neq('id', companyId)
          .maybeSingle();

        if (existingCompany) {
          throw new Error('CIN already exists for another company. Please use a different CIN.');
        }
      }

      // Check if PAN is being changed and if it conflicts with another company
      if (currentCompany && formData.pan !== currentCompany.pan) {
        const { data: existingCompany } = await supabase
          .from('companies')
          .select('id')
          .eq('pan', formData.pan)
          .neq('id', companyId)
          .maybeSingle();

        if (existingCompany) {
          throw new Error('PAN already exists for another company. Please use a different PAN.');
        }
      }

      let logoUrl = formData.logo_url;
      if (formData.logo) {
        logoUrl = await uploadDocument(formData.logo, 'logo');
      }

      const uploadedDashboardImages = await Promise.all(
        formData.new_dashboard_images.map(file => uploadDocument(file, 'dashboard'))
      );
      const finalDashboardImages = [...formData.dashboard_images, ...uploadedDashboardImages];

      const { error: updateError } = await supabase
        .from('companies')
        .update({
          name: formData.name,
          cin: formData.cin,
          pan: formData.pan,
          address: formData.address,
          csr_contact: formData.csr_contact,
          logo_url: logoUrl,
          dashboard_images: finalDashboardImages,
          profile_complete: true // Ensure it's marked as complete
        })
        .eq('id', companyId);

      if (updateError) {
        // Check if it's a duplicate key error
        if (updateError.code === '23505') {
          if (updateError.message.includes('cin')) {
            throw new Error('CIN already exists for another company. Please use a different CIN.');
          } else if (updateError.message.includes('pan')) {
            throw new Error('PAN already exists for another company. Please use a different PAN.');
          }
        }
        throw updateError;
      }

      // Update state if logo was uploaded
      if (formData.logo && logoUrl) {
        setFormData(prev => ({
          ...prev,
          logo_url: logoUrl,
          logo: null
        }));

        let path = logoUrl;
        if (path.includes('/object/public/company-documents/')) {
          path = path.split('/object/public/company-documents/')[1].split('?')[0];
        } else if (path.includes('/company-documents/')) {
          path = path.split('/company-documents/')[1].split('?')[0];
        } else if (path.startsWith('company-documents/')) {
          path = path.replace('company-documents/', '');
        }

        if (path) {
          const decodedPath = decodeURIComponent(path);
          const { data, error } = await supabase.storage.from('company-documents').createSignedUrl(decodedPath, 3600);
          if (error) console.error("Error fetching signed URL post save:", error);
          if (data?.signedUrl) {
            setLogoPreviewUrl(data.signedUrl);
          }
        }

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

      if (formData.new_dashboard_images.length > 0) {
        setFormData(prev => ({
          ...prev,
          dashboard_images: finalDashboardImages,
          new_dashboard_images: []
        }));
        if (dashboardInputRef.current) {
          dashboardInputRef.current.value = '';
        }
        
        // Reload page to get new signed URLs easily
        window.location.reload();
      }

      setSuccess('Company profile updated successfully!');
      setTimeout(() => {
        navigate(`/client/company/dashboard`);
      }, 1500);
    } catch (err: any) {
      console.error('Error updating company:', err);
      setError(err.message || 'Failed to update company profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '2rem 0' }}>
      <div className="form-container">
        <div className="form-section">
          <h1>Company Settings</h1>
          <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
            Update your company profile information and business units.
          </p>

          {error && (
            <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem', background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '6px', border: '1px solid #fca5a5' }}>
              {error}
            </div>
          )}

          {success && (
            <div className="toast toast-success" style={{ position: 'relative', marginBottom: '1rem' }}>
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Company Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter company name"
              />
            </div>

            <div className="form-group">
              <label>Company Logo</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1rem' }}>
                {(logoPreviewUrl || formData.logo_url) && !formData.logo && (
                  <div style={{ position: 'relative' }}>
                    <img
                      src={logoPreviewUrl || formData.logo_url!}
                      alt="Current logo"
                      style={{ height: '60px', maxWidth: '150px', objectFit: 'contain', borderRadius: '4px', border: '1px solid var(--border-light)' }}
                    />
                  </div>
                )}
                {formData.logo && (
                  <div style={{ position: 'relative' }}>
                    <img
                      src={URL.createObjectURL(formData.logo)}
                      alt="New logo preview"
                      style={{ height: '60px', maxWidth: '150px', objectFit: 'contain', borderRadius: '4px', border: '1px solid var(--border-light)' }}
                    />
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={(e) => setFormData(prev => ({ ...prev, logo: e.target.files?.[0] || null }))}
                    className="file-input"
                  />
                  {formData.logo && (
                    <p style={{ fontSize: '0.8rem', color: '#10b981', marginTop: '0.25rem' }}>
                      ✓ New selection: {formData.logo.name}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Dashboard Background Images (Optional)</label>
              <p style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                Upload multiple images to be used as a floating background banner on your dashboard.
              </p>
              
              {/* Existing Images Gallery */}
              {dashboardImagePreviewUrls.length > 0 && (
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  {dashboardImagePreviewUrls.map((url, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img 
                        src={url} 
                        alt={`Dashboard bg ${i}`} 
                        style={{ height: '80px', width: '140px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border-light)' }} 
                      />
                      <button 
                        type="button"
                        onClick={() => {
                          const newUrls = [...dashboardImagePreviewUrls];
                          newUrls.splice(i, 1);
                          setDashboardImagePreviewUrls(newUrls);
                          
                          const newDbImages = [...formData.dashboard_images];
                          newDbImages.splice(i, 1);
                          setFormData(prev => ({ ...prev, dashboard_images: newDbImages }));
                        }}
                        style={{
                          position: 'absolute', top: '-8px', right: '-8px', background: '#ef4444', color: '#fff',
                          border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload New Images */}
              <div style={{ flex: 1 }}>
                <input
                  type="file"
                  ref={dashboardInputRef}
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setFormData(prev => ({ ...prev, new_dashboard_images: [...prev.new_dashboard_images, ...files] }));
                  }}
                  className="file-input"
                />
                {formData.new_dashboard_images.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    {formData.new_dashboard_images.map((f, i) => (
                      <p key={i} style={{ fontSize: '0.8rem', color: '#10b981', marginTop: '0.25rem' }}>
                        ✓ New selection: {f.name}
                        <button 
                          type="button" 
                          onClick={() => {
                            const newFiles = [...formData.new_dashboard_images];
                            newFiles.splice(i, 1);
                            setFormData(prev => ({ ...prev, new_dashboard_images: newFiles }));
                            if (newFiles.length === 0 && dashboardInputRef.current) dashboardInputRef.current.value = '';
                          }}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', marginLeft: '8px', padding: 0 }}
                        >
                          remove
                        </button>
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>CIN (Corporate Identity Number) *</label>
                <input
                  type="text"
                  name="cin"
                  value={formData.cin}
                  onChange={handleChange}
                  required
                  placeholder="U12345AB1234PLC123456"
                />
              </div>

              <div className="form-group">
                <label>PAN (Permanent Account Number) *</label>
                <input
                  type="text"
                  name="pan"
                  value={formData.pan}
                  onChange={handleChange}
                  required
                  placeholder="ABCDE1234F"
                  maxLength={10}
                />
              </div>
            </div>

            <h2 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Company Address</h2>

            <div className="form-group">
              <label>Street Address *</label>
              <input
                type="text"
                name="address.street"
                value={formData.address.street}
                onChange={handleChange}
                required
                placeholder="Building, Street"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  name="address.state"
                  value={formData.address.state}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Pincode *</label>
                <input
                  type="text"
                  name="address.pincode"
                  value={formData.address.pincode}
                  onChange={handleChange}
                  required
                  pattern="[0-9]{6}"
                  maxLength={6}
                />
              </div>

              <div className="form-group">
                <label>Country *</label>
                <input
                  type="text"
                  name="address.country"
                  value={formData.address.country}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <h2 style={{ marginTop: '2rem', marginBottom: '1rem' }}>CSR Contact Information</h2>

            <div className="form-group">
              <label>Contact Name *</label>
              <input
                type="text"
                name="csr_contact.name"
                value={formData.csr_contact.name}
                onChange={handleChange}
                required
                placeholder="Full name"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="csr_contact.email"
                  value={formData.csr_contact.email}
                  onChange={handleChange}
                  required
                  placeholder="contact@company.com"
                />
              </div>

              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  name="csr_contact.phone"
                  value={formData.csr_contact.phone}
                  onChange={handleChange}
                  required
                  placeholder="+91 9876543210"
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate(`/client/company/dashboard`)}
                disabled={saving}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;
